import { getIsoWeek } from '@/utils/date';

import type { AxisTick, AxisTickUnit } from '../types';

/**
 * The track is at least 47rem wide (the board's 65rem less the 18rem label column), so this
 * many ticks still leave about 53px each: enough for "Week 41" or "Jan 2026".
 */
export const MAX_AXIS_TICKS = 14;

/** How long one tick lasts: a week, month or quarter, or a number of years. */
interface TickScale {
    unit: AxisTickUnit;
    /** Years per tick; ticks start on a multiple of it (2020, 2025, …). */
    years: number;
}

/** Finest first; the first scale whose ticks fit in the allowed count wins. */
const SCALES: TickScale[] = [
    { unit: 'week', years: 1 },
    { unit: 'month', years: 1 },
    { unit: 'quarter', years: 1 },
    ...[1, 2, 5, 10, 20, 50, 100].map((years) => ({ unit: 'year' as const, years })),
];

/** Without a measured width, a tick narrower than this share of a whole period gets no label. */
const MIN_LABELLED_SHARE = 0.5;

/** Room one header label needs: "Week 52", "Dec 2024" or "Q4 2024" in overline capitals. */
export const MIN_TICK_PX = 64;

/** Local Monday 00:00 of the ISO week `date` falls in. */
export function startOfIsoWeek(date: Date): Date {
    const monday = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
    return monday;
}

/** Local start of the week, month, quarter or run of years that `date` falls in. */
function periodStart(date: Date, scale: TickScale): Date {
    const year = date.getFullYear();
    const month = date.getMonth();
    switch (scale.unit) {
        case 'week':
            return startOfIsoWeek(date);
        case 'month':
            return new Date(year, month, 1);
        case 'quarter':
            return new Date(year, month - (month % 3), 1);
        case 'year':
            return new Date(year - (year % scale.years), 0, 1);
    }
}

function nextPeriod(start: Date, scale: TickScale): Date {
    switch (scale.unit) {
        case 'week':
            return new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7);
        case 'month':
            return new Date(start.getFullYear(), start.getMonth() + 1, 1);
        case 'quarter':
            return new Date(start.getFullYear(), start.getMonth() + 3, 1);
        case 'year':
            return new Date(start.getFullYear() + scale.years, 0, 1);
    }
}

/** The ISO week-year: the calendar year of the week's Thursday. */
export function isoWeekYear(monday: Date): number {
    return new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 3).getFullYear();
}

function periodFields(start: Date, unit: AxisTickUnit) {
    const year = start.getFullYear();
    switch (unit) {
        case 'week':
            return { unit, week: getIsoWeek(start), year: isoWeekYear(start) } as const;
        case 'month':
            return { unit, month: start.getMonth(), year } as const;
        case 'quarter':
            return { unit, quarter: Math.floor(start.getMonth() / 3) + 1, year } as const;
        case 'year':
            return { unit, year } as const;
    }
}

function ticksOf(
    scale: TickScale,
    startMs: number,
    endMs: number,
    trackPx: number | null
): AxisTick[] {
    const span = endMs - startMs;
    // A measured track labels only the ticks wide enough for a label, so none is cut off.
    const hasRoom = (from: number, to: number, periodMs: number) =>
        trackPx === null
            ? to - from >= periodMs * MIN_LABELLED_SHARE
            : ((to - from) / span) * trackPx >= MIN_TICK_PX;
    const ticks: AxisTick[] = [];
    for (
        let start = periodStart(new Date(startMs), scale);
        start.getTime() < endMs;
        start = nextPeriod(start, scale)
    ) {
        const next = nextPeriod(start, scale).getTime();
        const from = Math.max(start.getTime(), startMs);
        const to = Math.min(next, endMs);
        ticks.push({
            key: `${scale.unit}-${start.getTime()}`,
            leftPct: ((from - startMs) / span) * 100,
            widthPct: ((to - from) / span) * 100,
            showLabel: hasRoom(from, to, next - start.getTime()),
            ...periodFields(start, scale.unit),
        });
    }
    return ticks;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** A whole period's length, near enough to tell whether its label fits. */
function periodMs(scale: TickScale): number {
    switch (scale.unit) {
        case 'week':
            return 7 * DAY_MS;
        case 'month':
            return 30.44 * DAY_MS;
        case 'quarter':
            return 91.31 * DAY_MS;
        case 'year':
            return 365.25 * DAY_MS * scale.years;
    }
}

/**
 * Header ticks for the axis, coarsening from weeks until there are at most `maxTicks`, so
 * every label has room on the track they share. Given the track's measured width, a scale
 * whose whole periods are too narrow for a label is skipped, and a partial period at either
 * end is labelled only where its label fits. Week and month labels carry no year: the year
 * row above them names it (`buildYearBands`).
 */
export function buildAxisTicks(
    startMs: number,
    endMs: number,
    maxTicks: number = MAX_AXIS_TICKS,
    trackPx: number | null = null
): AxisTick[] {
    for (const scale of SCALES) {
        // At a measured width a scale whose whole periods can't hold a label is skipped, or a
        // short cycle zoomed out onto the minimum track would draw weeks with no numbers.
        if (trackPx !== null && (periodMs(scale) / (endMs - startMs)) * trackPx < MIN_TICK_PX) {
            continue;
        }
        const ticks = ticksOf(scale, startMs, endMs, trackPx);
        if (ticks.length <= maxTicks) return ticks;
    }
    return ticksOf(SCALES[SCALES.length - 1], startMs, endMs, trackPx);
}
